import { z } from "zod";

/**
 * Shared DTO cho Admin Support Inbox + Macro tiếng Việt (US-AE11.F4-0040 · @UC9103
 * support-inbox-zalo-macro). MỘT nguồn sự thật cho api (S01) + admin web (S03).
 *
 * Ticket = hội thoại hỗ trợ (Zalo OA inbound 2-chiều / in-app báo lỗi) gắn user/group. Message = dòng
 * hội thoại (inbound user / outbound admin reply). Macro = kho câu trả lời mẫu tiếng Việt (canned reply).
 *
 * BẤT BIẾN (invariants):
 * - G2: inbox KHÔNG BAO GIỜ ghi sổ cái — chỉ hội thoại hỗ trợ. DTO KHÔNG có field ghi tiền.
 * - C2/G4 no-PII/secret thô: ticket trả id user/group (con trỏ) — KHÔNG email/phone; KHÔNG token/secret.
 * - No-SMS: kênh CHỈ zalo_oa | in_app (KHÔNG SMS).
 * - `.strict()` cho body ghi (reply/transition) — chống over-post/field lạ.
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin lib/status.ts)
// ============================================================

/** Nguồn ticket (DR-2). zalo_oa = webhook inbound 2-chiều; in_app = báo lỗi từ app (AC4 app-side). */
export const supportSourceSchema = z.enum(["zalo_oa", "in_app"]);
export type SupportSource = z.infer<typeof supportSourceSchema>;

/**
 * Trạng thái vòng đời ticket (DR-1). open = mới; pending = đang xử lý (admin đã reply); resolved = đã
 * xử lý; closed = đã đóng (cuối, không tái mở).
 */
export const supportTicketStatusSchema = z.enum(["open", "pending", "resolved", "closed"]);
export type SupportTicketStatus = z.infer<typeof supportTicketStatusSchema>;

/** Chiều message: inbound (từ user qua Zalo OA/in-app) | outbound (admin reply). */
export const supportMessageDirectionSchema = z.enum(["inbound", "outbound"]);
export type SupportMessageDirection = z.infer<typeof supportMessageDirectionSchema>;

// ============================================================
// Inbox item (list) + detail thread
// ============================================================

/** 1 dòng ticket trên inbox list. userId/groupId = id con trỏ (KHÔNG PII thô — resolve tên ở UI ref). */
export const inboxItemSchema = z
  .object({
    id: z.string(),
    source: supportSourceSchema,
    userId: z.string().nullable(),
    groupId: z.string().nullable(),
    status: supportTicketStatusSchema,
    subject: z.string().nullable(),
    /** Preview = nội dung message gần nhất (rút gọn — KHÔNG PII/secret thô). */
    preview: z.string().nullable(),
    /** Thời điểm hoạt động gần nhất (updatedAt) — ISO 8601. */
    lastMessageAt: z.string(),
  })
  .strict();

export type InboxItem = z.infer<typeof inboxItemSchema>;

/** Envelope list inbox: items + cursor pageInfo `{ data, pageInfo }`. */
export const inboxResponseSchema = z
  .object({
    data: z.array(inboxItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type InboxResponse = z.infer<typeof inboxResponseSchema>;

/** 1 dòng hội thoại trong thread (KHÔNG PII/secret thô). */
export const supportMessageSchema = z
  .object({
    id: z.string(),
    direction: supportMessageDirectionSchema,
    body: z.string(),
    macroKey: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export type SupportMessage = z.infer<typeof supportMessageSchema>;

/**
 * Deep-link (AC3) — con trỏ tới màn liên quan cho UI mở nhanh. userDetailUrl = route user detail admin
 * (nếu ticket gắn user); disputeCaseId = case tranh chấp liên quan (nếu có — soft-link 0038).
 */
export const supportTicketLinksSchema = z
  .object({
    userDetailUrl: z.string().nullable(),
    disputeCaseId: z.string().nullable(),
  })
  .strict();

export type SupportTicketLinks = z.infer<typeof supportTicketLinksSchema>;

/** Chi tiết ticket = item + thread message (thứ tự thời gian) + deep-link. */
export const ticketDetailSchema = inboxItemSchema
  .extend({
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
    messages: z.array(supportMessageSchema),
    links: supportTicketLinksSchema,
  })
  .strict();

export type TicketDetail = z.infer<typeof ticketDetailSchema>;

// ============================================================
// Query (GET /admin/support/inbox)
// ============================================================

export const INBOX_LIST_MAX_LIMIT = 100;

/** Query list — filter status/source, cursor + limit. `.strict()` chặn param lạ. */
export const inboxListQuerySchema = z
  .object({
    status: supportTicketStatusSchema.optional(),
    source: supportSourceSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(INBOX_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type InboxListQuery = z.infer<typeof inboxListQuerySchema>;

// ============================================================
// Reply (POST /admin/support/tickets/:id/reply)
// ============================================================

export const SUPPORT_REPLY_MAX_LENGTH = 2000;
export const SUPPORT_MACRO_KEY_MAX_LENGTH = 64;
export const SUPPORT_CONTEXT_MAX_LENGTH = 500;

/**
 * Body reply. ≥1 trong { body, macroKey } (refine). macroKey → render bodyVi từ kho macro + chèn
 * context (tuỳ chọn). body = text tự do khi không dùng macro. `.strict()`. KHÔNG field ghi tiền (G2).
 */
export const replySchema = z
  .object({
    body: z.string().min(1).max(SUPPORT_REPLY_MAX_LENGTH).optional(),
    macroKey: z.string().max(SUPPORT_MACRO_KEY_MAX_LENGTH).optional(),
    /** Ngữ cảnh chèn thêm sau macro (vd tên/số kỳ) — text tự do (KHÔNG PII/secret thô bắt buộc). */
    context: z.string().max(SUPPORT_CONTEXT_MAX_LENGTH).optional(),
  })
  .strict()
  .refine((v) => Boolean(v.body) || Boolean(v.macroKey), {
    message: "Cần ít nhất nội dung (body) hoặc macro (macroKey).",
    path: ["body"],
  });

export type ReplyBody = z.infer<typeof replySchema>;

// ============================================================
// Transition status (PATCH /admin/support/tickets/:id)
// ============================================================

/**
 * Body chuyển trạng thái ticket. status ∈ {pending,resolved,closed} (open KHÔNG set qua transition —
 * chỉ tiến tới). `.strict()`. KHÔNG field ghi/sửa tiền (G2 — inbox chỉ hội thoại).
 */
export const transitionTicketSchema = z
  .object({
    status: z.enum(["pending", "resolved", "closed"]),
  })
  .strict();

export type TransitionTicketBody = z.infer<typeof transitionTicketSchema>;

// ============================================================
// Macro (GET /admin/support/macros)
// ============================================================

/** 1 macro tiếng Việt (canned reply). key ổn định (chọn ở UI + render server); bodyVi = nội dung. */
export const macroSchema = z
  .object({
    key: z.string(),
    title: z.string(),
    bodyVi: z.string(),
  })
  .strict();

export type Macro = z.infer<typeof macroSchema>;

/** Envelope list macro `{ data }` (không cần cursor — kho nhỏ). */
export const macroListResponseSchema = z
  .object({
    data: z.array(macroSchema),
  })
  .strict();

export type MacroListResponse = z.infer<typeof macroListResponseSchema>;

// ============================================================
// Zalo OA inbound webhook payload (POST /webhooks/zalo-oa)
// ============================================================

export const ZALO_INBOUND_TEXT_MAX_LENGTH = 4000;

/**
 * Payload webhook Zalo OA INBOUND (UNTRUSTED — parse SAU verify chữ ký). Trích tối thiểu: sender.id
 * (dedupe ticket), message.msg_id (dedupe idempotent), message.text (nội dung). KHÔNG `.strict()` — Zalo
 * gửi nhiều field phụ (event_name, timestamp…) mà ta bỏ qua an toàn. userId/groupId map ở tầng app-side
 * (AC4) — task này chỉ nhận zalo_oa.
 */
export const zaloInboundWebhookSchema = z.object({
  sender: z.object({ id: z.string().min(1) }),
  message: z.object({
    msg_id: z.string().min(1),
    text: z.string().min(1).max(ZALO_INBOUND_TEXT_MAX_LENGTH),
  }),
});

export type ZaloInboundWebhook = z.infer<typeof zaloInboundWebhookSchema>;
